import _ from "lodash";
import React, { useState, useCallback } from "react";
import { connect } from "react-redux";
import classNames from "classnames";

// Hooks
import { useI18n } from "Hooks";

// Utils
import {
  getTextForClipboard,
  extractStateAndContent,
} from "../../modules/Utils";
import { copyToClipboardWithMimeType } from "Utils/CopyToClipboard";
import {
  getAssessmentFieldValue,
  CODE_EDITOR_MODALITY,
  TODDLE_DOC_MODALITY,
  WORKBOOK_MODALITY,
  VIDEO_EXPLORER_MODALITY,
  SEARCH,
  RUN_PYTHON_CODE,
} from "Utils/AIUtils";
import { getIsAIEditDeleteButtonEnabled } from "Utils/ACLStoreUtils";
import { AI_TUTOR_LEARNING_TOOLS } from "@toddle-edu/common-utils";

// Modules
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { updateAIConversation } from "modules/Services";

// Constants
import { STREAMING, THINKING } from "Constants/stringConstants";
import { colors } from "Constants";

// Design system
import { Button, Avatar } from "@toddle-edu/ds-web";
import {
  ReloadArrowOutlined,
  CopyOutlined,
  SquareFilled,
  VolumeHighOutlined,
  DeleteOutlined,
  PencilOutlined,
} from "@toddle-edu/ds-icons";

// Components
import AIChat from "UIComponents/AIChat";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";
import GenerateTranscriptsButton from "AppComponents/AIComponents/GenerateTranscriptsButton";
import AITutorAnalysis from "AppComponents/AITutorAnalysis";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import CompactAudioWaveformPlayer from "UIComponents/CompactAudioWaveformPlayer";
import StreamingAudioPlayer from "UIComponents/StreamingAudioPlayer";
import SeeMoreLessHtml from "UIComponents/SeeMoreLessHtml";

// Scss
import classes from "./AIChatBody.scss";

const styles = {
  aiFeedbackStrip: {
    background: "var(--surface-primary-enabled)",
    padding: 0,
    borderRadius: "8px",
  },
  innerContainerStyle: {
    padding: "16px",
  },
  evaluationContainerStyle: {
    padding: "12px 0px",
  },
  seeMoreContainerStyle: {
    color: "var(--text-secondary)",
  },
};

const AIChatBody = props => {
  const {
    source,
    messages,
    isEditMode,
    setToastMsg,
    currentAIState,
    aiConversation,
    assessmentDetails,
    flaggedMessagesCount,
    onClickStartNewSession,
    currentlyAudioPlayingForItemId,
    isAudioGenerating,
    onSpeakMessageClick,
    updateAIConversation,
    ttsAudioRegistry,
  } = props;

  const { t } = useI18n();

  // ======= states ========
  const [showFullScreenLoader, setShowFullScreenLoader] = useState(false);

  // ======= variables ========
  const assessmentId = _.get(assessmentDetails, "id");
  const aiConversationId = _.get(aiConversation, "id");
  const conversationMetadata = _.get(aiConversation, "metadata", {});
  const defaultPlaybackSpeed = _.get(
    conversationMetadata,
    "tutorAudioSpeed",
    "1"
  );

  const allFields = _.get(assessmentDetails, "allFields", []);
  const tutorCommunicationMode = getAssessmentFieldValue({
    fields: allFields,
    uid: "tutorCommunicationMode",
  });

  const isTutorSpeakingOnly = tutorCommunicationMode === "speaking_only";
  const isTutorWritingOnly = tutorCommunicationMode === "writing_only";

  const getActionList = (node, index) => {
    const { id, content } = node || {};

    const isCurrentAudioPlaying = currentlyAudioPlayingForItemId == id;

    const audioIcon = isCurrentAudioPlaying ? (
      <SquareFilled />
    ) : (
      <VolumeHighOutlined />
    );

    const isLastMessage = index === _.size(messages) - 1;

    const isAudioDisabled =
      _.includes(id, "NEW") ||
      (_.includes([STREAMING, THINKING], currentAIState) && isLastMessage) ||
      (isAudioGenerating && isCurrentAudioPlaying);

    const actions = [];

    if (!isTutorSpeakingOnly && !_.isEmpty(content)) {
      actions.push({
        key: "copy",
        icon: <CopyOutlined />,
        tooltipLabel: "common:copy",
      });
    }

    if (!isTutorSpeakingOnly && !isTutorWritingOnly && !_.isEmpty(content)) {
      actions.push({
        key: "audio",
        icon: audioIcon,
        disabled: isAudioDisabled,
        tooltipLabel: "common:read_aloud",
      });
    }

    if (getIsAIEditDeleteButtonEnabled()) {
      actions.push(
        {
          key: "internalEdit",
          icon: <PencilOutlined />,
          tooltipLabel: "common:edit",
        },
        {
          key: "delete",
          icon: <DeleteOutlined />,
          tooltipLabel: "common:delete",
        }
      );
    }

    return actions;
  };

  const onClickAction = params => {
    const { key, node } = params;

    const { content } = node || {};

    switch (key) {
      case "copy": {
        const { plainText, htmlText } = getTextForClipboard({
          content: customContentParser(content),
        });

        copyToClipboardWithMimeType(
          {
            text: plainText,
            html: htmlText,
          },
          () => {
            setToastMsg({
              msg: "toastMsgs:copied_capital",
              type: "success",
            });
          }
        );
        break;
      }
      case "audio": {
        onSpeakMessageClick(node);
        break;
      }
    }
  };

  const customContentParser = content => {
    if (_.isString(content)) {
      const { content: extractedContent, output: outputTagContent } =
        extractStateAndContent(content) || {};
      content = extractedContent || outputTagContent;
    }

    if (_.endsWith(content, "<")) {
      return content.slice(0, -1);
    }

    return content;
  };

  const checkAssistantMessageVisibility = index => {
    const lastAssistantMessageIndex = _.findLastIndex(
      messages,
      ({ node }) => node?.authorType === "assistant"
    );

    const isLastAssistantMessage = index === lastAssistantMessageIndex;
    const isLastMessageFromUser =
      _.get(_.last(messages), "node.authorType") === "user";

    return !(
      isTutorSpeakingOnly &&
      isLastAssistantMessage &&
      !isLastMessageFromUser &&
      !_.isEmpty(currentAIState)
    );
  };

  const updateConversationAudioSpeed = speed => {
    const oldMetadata = _.get(aiConversation, "metadata", {});

    if (oldMetadata.tutorAudioSpeed === speed) return;

    updateAIConversation({
      input: {
        id: aiConversationId,
        metadata: {
          ...oldMetadata,
          tutorAudioSpeed: speed,
        },
      },
    });
  };

  // ======= components ========
  const renderCustomMessage = ({ message }) => {
    const { id, authorType, metadata, content, usageMetricsId } = message || {};

    switch (authorType) {
      case "flaggedMessage":
      case "tutorUpdated": {
        const message = _.includes(content, "ai:") ? t(content) : content;

        const tutorUpdatedTextClass = classNames(
          classes.tutorUpdatedText,
          "text-body-s",
          { [classes.italic]: authorType === "flaggedMessage" }
        );

        const textContainerClass = classNames({
          [classes.tutorUpdated]: true,
          [classes.flaggedMessage]: authorType === "flaggedMessage",
        });

        return (
          <div key={id} className={classes.tutorUpdatedContainer}>
            <span className={classes.line} />
            <span className={textContainerClass}>
              <span className={tutorUpdatedTextClass}>{message}</span>
            </span>
            <span className={classes.line} />
          </div>
        );
      }
      case "tutorUpdatedWithResetChat": {
        const onClickResetChat = async () => {
          setShowFullScreenLoader(true);
          flaggedMessagesCount.current = 0;
          await onClickStartNewSession();
          setShowFullScreenLoader(false);
        };

        return (
          <div key={id} className={classes.tutorUpdatedContainer}>
            <span className={classes.line} />
            <span className={classes.tutorUpdated}>
              <span
                className={classNames(classes.tutorUpdatedText, "text-body-s")}
              >
                {t(content)}
              </span>
              <span className={classes.dot} />

              <Button
                dsVersion={"2.0"}
                icon={<ReloadArrowOutlined />}
                variant={"progressive"}
                type={"inline"}
                size={"small"}
                onClick={onClickResetChat}
                disabled={!isEditMode}
                testId={`${id}-aiTutorChat-tutorUpdatedWithResetChat`}
              >
                {t("ai:reset_chat")}
              </Button>
            </span>
            <span className={classes.line} />
          </div>
        );
      }
      case "aiTutorEvaluation": {
        return (
          <div key={id} className={classes.aiTutorEvaluationChatBox}>
            <Avatar
              size={"small"}
              shape={"circle"}
              shade={"dark"}
              className={classes.noShrink}
              name={"A I"}
              color={"pink"}
              dsVersion={"2.0"}
            />
            <div className={classes.aiTutorEvaluationContainer}>
              <div className={classes.aiTutorEvaluation}>
                <AITutorAnalysis
                  assessmentId={assessmentId}
                  aiConversationId={aiConversationId}
                  innerContainerStyle={styles.innerContainerStyle}
                  evaluationContainerStyle={styles.evaluationContainerStyle}
                  source={source}
                />
                <div className={classes.feedBackStripContainer}>
                  <AIFeedbackStrip
                    {...metadata}
                    showLikeButton={false}
                    showWarningText={false}
                    showDislikeButton={false}
                    usageMetricsId={usageMetricsId}
                    containerStyle={styles.aiFeedbackStrip}
                    sourceFrom={"LESSON_PLANNING_ASSISTANT"}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      }
    }
  };

  const customAssistantMessageRenderer = ({ node }) => {
    const { id, metadata, content, authorType } = node || {};
    const { audioUrl } = metadata || {};

    const isAssistantMessage = authorType === "assistant";
    const entry = ttsAudioRegistry?.get(id);
    const audioElement = entry?.audioEl;

    const attachment = {
      id: `${id}-audio`,
      url: audioUrl,
    };

    let compactAudioWaveformPlayerProps = {
      playerIconClass: classes.playerIcon,
      playbackSpeedTriggerClass: classes.playbackSpeedTrigger,
      activePlaybackSpeedTriggerClass: classes.activePlaybackSpeedTrigger,
      isAppearanceLikeMediaCard: true,
    };

    if (isAssistantMessage) {
      compactAudioWaveformPlayerProps = {
        ...compactAudioWaveformPlayerProps,
        progressColor: colors.decorativeSurfaceTwoD,
        cursorColor: colors.decorativeSurfaceTwoD,
        containerStyle: {
          backgroundColor: "var(--decorative-background-subtle-violet)",
        },
        playerButtonIconStyle: {
          color: colors.decorativeSurfaceTwoD,
        },
        playbackSpeedButtonProps: {
          type: "plain",
        },
        buttonElement: (
          <GenerateTranscriptsButton
            content={customContentParser(content)}
            id={id}
          />
        ),
        showDownload: false,
      };
    }

    const isAudioReady = entry?.isReady;
    const hasAudioError = entry?.hasError || audioElement?.error;
    const hasAudioEnded = entry?.hasEnded;

    // Streaming: audioElement exists and is ready (data appended), not ended, and no saved audioUrl yet
    const isStreaming =
      audioElement && isAudioReady && !hasAudioError && !hasAudioEnded;

    // Show streaming player while audio is playing but not yet saved
    if (isStreaming) {
      return (
        <div className={classes.audioAttachment}>
          <StreamingAudioPlayer
            id={id}
            audioElement={audioElement}
            defaultPlaybackSpeed={defaultPlaybackSpeed}
            onAudioSpeedChange={updateConversationAudioSpeed}
            {...compactAudioWaveformPlayerProps}
          />
        </div>
      );
    }

    // Show waveform player when we have the saved audioUrl
    if (!_.isEmpty(attachment?.url)) {
      return (
        <div className={classes.audioAttachment}>
          <CompactAudioWaveformPlayer
            attachment={attachment}
            defaultPlaybackSpeed={defaultPlaybackSpeed}
            onAudioSpeedChange={updateConversationAudioSpeed}
            {...compactAudioWaveformPlayerProps}
          />

          {source === "STUDENT_ASSIGNMENT_SUBMISSION" ? (
            <div className={classes.messageText}>
              <SeeMoreLessHtml
                truncateLength={300}
                fullSource={customContentParser(content)}
                containerStyle={styles.seeMoreContainerStyle}
                seeLessText={t("common:view_less")}
                seeMoreText={t("common:view_more")}
              />
            </div>
          ) : null}
        </div>
      );
    }
  };

  const customLoadingTextRenderer = useCallback(currentAIState => {
    switch (currentAIState) {
      case CODE_EDITOR_MODALITY:
      case TODDLE_DOC_MODALITY:
      case WORKBOOK_MODALITY:
      case VIDEO_EXPLORER_MODALITY: {
        const { label } =
          _.find(AI_TUTOR_LEARNING_TOOLS, { key: currentAIState }) || {};

        if (label) {
          return t("ai:setting_up_the_label", { label: t(label) });
        }

        return t("ai:thinking");
      }
      case SEARCH: {
        return t("ai:browsing_the_internet");
      }
      case RUN_PYTHON_CODE: {
        return t("ai:creating_an_image");
      }
    }
  }, []);

  return (
    <>
      <AIChat.Body
        showLikeDislikeButton={false}
        onClickAction={onClickAction}
        customAIActionsList={getActionList}
        renderCustomMessage={renderCustomMessage}
        customContentParser={customContentParser}
        checkAssistantMessageVisibility={checkAssistantMessageVisibility}
        customAssistantMessageRenderer={
          isTutorSpeakingOnly ? customAssistantMessageRenderer : null
        }
        sourceFrom={source}
        customLoadingTextRenderer={customLoadingTextRenderer}
      />

      {showFullScreenLoader ? <FullScreenLoader /> : null}
    </>
  );
};

const mapActionCreators = {
  setToastMsg,
  updateAIConversation,
};

export default connect(null, mapActionCreators)(AIChatBody);
